import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, SlidersHorizontal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  skeleton_trailers: "Skeleton Trailers",
  flatbed_trailers: "Flatbed Trailers",
  metals: "Ferrous & Non-Ferrous Metals",
  cargo: "General Cargo",
};

export default function CatalogPage() {
  const { category: urlCategory } = useParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(urlCategory || "all");
  const [condition, setCondition] = useState("all");

  useEffect(() => {
    if (urlCategory) setCategory(urlCategory);
  }, [urlCategory]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = {};
        if (category && category !== "all") params.category = category;
        if (condition && condition !== "all") params.condition = condition;
        if (search) params.search = search;
        const res = await axios.get(`${API}/products`, { params });
        setProducts(res.data.products || []);
        setTotal(res.data.total || 0);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
      }
    };
    const debounce = setTimeout(fetchProducts, 300);
    return () => clearTimeout(debounce);
  }, [category, condition, search]);

  return (
    <div data-testid="catalog-page" className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-10">
          <p className="text-[#DC143C] text-sm uppercase tracking-[0.2em] font-semibold mb-3">
            Our Inventory
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-2">
            {urlCategory ? CATEGORY_LABELS[urlCategory] || "Products" : "All Products"}
          </h1>
          <p className="text-[#9CA3AF] text-base">
            {total} {total === 1 ? "item" : "items"} available
          </p>
        </div>

        {/* Filters */}
        <div
          data-testid="catalog-filters"
          className="flex flex-col sm:flex-row gap-4 mb-10 p-4 bg-[#0A0A12] border border-white/10 rounded-lg"
        >
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input
              data-testid="catalog-search"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-[#05050A] border-white/15 focus:border-[#DC143C] text-white placeholder:text-[#9CA3AF]/50"
            />
          </div>
          <div className="flex gap-4">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="catalog-category-filter" className="w-[180px] bg-[#05050A] border-white/15 text-white">
                <SlidersHorizontal size={14} className="mr-2 text-[#9CA3AF]" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A12] border-white/10 text-white">
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="skeleton_trailers">Skeleton Trailers</SelectItem>
                <SelectItem value="flatbed_trailers">Flatbed Trailers</SelectItem>
                <SelectItem value="metals">Metals</SelectItem>
                <SelectItem value="cargo">General Cargo</SelectItem>
              </SelectContent>
            </Select>
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger data-testid="catalog-condition-filter" className="w-[150px] bg-[#05050A] border-white/15 text-white">
                <SelectValue placeholder="Condition" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A12] border-white/10 text-white">
                <SelectItem value="all">All Conditions</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="used">Used</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-[#0A0A12] border border-white/10 rounded-lg overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-[#1E1E28]" />
                <div className="p-4 space-y-3">
                  <div className="h-3 bg-[#1E1E28] rounded w-1/3" />
                  <div className="h-4 bg-[#1E1E28] rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div data-testid="no-products" className="text-center py-24">
            <p className="text-[#9CA3AF] text-lg mb-2">No products found</p>
            <p className="text-[#9CA3AF]/60 text-sm">Try adjusting your filters or search terms</p>
          </div>
        )}
      </div>
    </div>
  );
}
